import { onUnmounted, isRef, type Ref } from 'vue';

/**
 * A single click-outside target:
 * - `Ref<Element | null>` — Vue template ref
 * - `Element` — plain DOM element
 * - `string` — CSS selector resolved at event time via `querySelectorAll`
 *   (e.g. `'#my-id'`, `'.my-class'`, `'[data-panel]'`). Useful when you
 *   don't have a direct element reference. Multiple matching elements all
 *   count as "inside".
 * - `null` — treated as inactive (no callbacks fired)
 */
export type ClickOutsideTarget = Ref<Element | null> | Element | string | null;

/**
 * Tracks pointer-down clicks and reports whether they land inside or outside one
 * or more given elements.
 *
 * @param {ClickOutsideTarget | ClickOutsideTarget[]} target
 *   One element, ref, or CSS selector string — or an array of any mix.
 *
 * @param {(event: PointerEvent) => void} onOutside
 *   Called when the click lands outside all targets.
 *
 * @param {(event: PointerEvent) => void} [onInside]
 *   Optional callback called when the click lands inside at least one target.
 *
 * @param {{ capture?: boolean }} [options]
 *   `capture: true` registers the listener in the capture phase (default: false).
 *   Use capture mode when inner elements call `stopPropagation` on bubble.
 *
 * @returns {{ stop: () => void }}
 *   `stop()` removes the listener early without waiting for `onUnmounted`.
 *
 * @example — single template ref
 *   const panel = ref(null);
 *   useClickOutside(panel, () => closePanel());
 *   // <div ref="panel"> ... </div>
 *
 * @example — multiple refs
 *   const panel = ref(null);
 *   const toolbar = ref(null);
 *   useClickOutside([panel, toolbar], () => closePanel());
 *
 * @example — plain element
 *   const el = document.getElementById('my-el');
 *   useClickOutside(el, () => closePanel());
 *
 * @example — CSS selector by ID
 *   useClickOutside('#my-panel', () => closePanel());
 *
 * @example — CSS selector by class (all matching elements count as inside)
 *   useClickOutside('.dropdown-menu', () => closeMenu());
 *
 * @example — mix of refs and selectors
 *   const trigger = ref(null);
 *   useClickOutside([trigger, '#floating-panel'], () => close());
 */
/** Resolves a single target to zero or more live DOM elements. */
function resolveTarget(t: ClickOutsideTarget): Element[] {
  if (t === null) return [];
  if (isRef(t)) return t.value ? [t.value] : [];
  if (typeof t === 'string') return Array.from(document.querySelectorAll(t));
  return [t];
}

export function useClickOutside(
  target: ClickOutsideTarget | ClickOutsideTarget[],
  onOutside: (event: PointerEvent) => void,
  onInside?: (event: PointerEvent) => void,
  options: { capture?: boolean } = {}
): { stop: () => void } {
  const { capture = false } = options;
  const targets: ClickOutsideTarget[] = Array.isArray(target) ? target : [target];

  function handlePointerDown(event: PointerEvent) {
    if (!(event.target instanceof Node)) return;

    // Resolve all targets at event time — string selectors query the live DOM,
    // refs read their current value. Nulls and empty selector results are discarded.
    const elements = targets.flatMap(resolveTarget);

    // If no target resolves to an element, treat as inactive and fire nothing.
    if (!elements.length) return;

    if (elements.some((el) => el.contains(event.target as Node))) {
      onInside?.(event);
    } else {
      onOutside(event);
    }
  }

  document.addEventListener('pointerdown', handlePointerDown, capture);

  function stop() {
    document.removeEventListener('pointerdown', handlePointerDown, capture);
  }

  onUnmounted(stop);

  return { stop };
}
